import { CustomTooltip, Spinner } from '@tcweb/connect-ui';
import { createMemo, For, JSX, Match, Show, Switch } from 'solid-js';
import { t } from '@trimble/i18n';
import { PROJECT_PLACEHOLDER_THUMBNAIL } from '@tcweb/connect-ui';
import { Project, Region } from '../types/project-select.types';
import { ThumbnailLoader } from '@trimble/ui-components/solidjs';
import {
  RESULT_LIMIT,
  getRegionalBaseUrl,
} from '@trimble/connect-project-providers';
import { useQuery, useQueryClient } from '@tanstack/solid-query';
import {
  client as tcTpcsClient,
  listallProjects,
  listallProjectsQueryKey,
} from '@data-access/connect/tc-tcps/generated';

interface RecentProjectsProps {
  region: Region;
  currentProjectId?: string;
  onProjectSelect(region: Region, project: Project): void;
}

export const RecentProjects = (props: RecentProjectsProps): JSX.Element => {
  const listItemClasses = ['first:border-t', 'border-b', 'border-base-200'];
  const queryClient = useQueryClient();

  // Define the query at the top level of the component, not inside a function
  const recentProjectsQuery = useQuery(() => {
    const region = props.region;
    const options = region
      ? {
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Range: `items=0-${String(RESULT_LIMIT - 1)}`,
          },
          query: {
            fullyLoaded: true,
            sort: '-lastVisited',
          },
          baseUrl: getRegionalBaseUrl(region),
        }
      : null;

    return {
      queryKey: listallProjectsQueryKey(options ?? {}),
      queryFn: async ({ signal }) => {
        if (!options) return [];
        const { data } = await listallProjects({
          ...options,
          signal,
          throwOnError: true,
        });
        return data;
      },
      enabled: !!region,
    };
  });

  const recentProjects = createMemo(() => {
    const data = recentProjectsQuery.data;
    if (!data) return [];

    return data.map((item) => ({
      id: item.id,
      name: item.name,
      thumbnail: 'thumbnail' in item ? item.thumbnail : undefined,
    }));
  });

  function getImageBlobUrl(imageUrl: string): Promise<string> {
    return queryClient.fetchQuery({
      queryKey: ['tc-tcps', 'image-blob', imageUrl],
      staleTime: Infinity,
      queryFn: async () => {
        const url = new URL(imageUrl);
        const response = await tcTpcsClient.request<Blob>({
          method: 'GET',
          url: `${url.pathname}${url.search}`,
          baseUrl: url.origin,
        });

        if (response.error) {
          return PROJECT_PLACEHOLDER_THUMBNAIL;
        } else {
          return URL.createObjectURL(response.data!);
        }
      },
    });
  }

  return (
    <div data-testid="recent-projects" class="flex flex-1 text-base-content">
      <Switch>
        <Match when={recentProjectsQuery.isLoading}>
          <div data-testid="recent-projects-spinner" class="flex-1">
            <Spinner class="!min-h-fit" show={true} />
          </div>
        </Match>
        <Match when={recentProjectsQuery.isError}>
          <div data-testid="recent-projects-error">
            {t('shared.ui.projectSelect.failedToLoadRecentProjects')}
          </div>
        </Match>
        <Match when={recentProjectsQuery.isSuccess}>
          <Show when={recentProjects().length === 0}>
            <div data-testid="recent-projects-no-projects">
              {t('shared.ui.projectSelect.noRecentProjects')}
            </div>
          </Show>
          <Show when={recentProjects().length > 0}>
            <ul
              class="dropdown-list project-list w-full border-base-200"
              data-testid="recent-projects-list"
            >
              <For each={recentProjects()}>
                {(project) => {
                  const isSelected = (): boolean =>
                    project.id === props.currentProjectId;

                  return (
                    <li
                      class={[
                        ...listItemClasses,
                        isSelected()
                          ? '!bg-primary-pale hover:!bg-primary-pale !text-primary font-semibold'
                          : '!text-base-content hover:!bg-gray-light',
                      ].join(' ')}
                      onClick={() => {
                        props.onProjectSelect(props.region, project);
                      }}
                    >
                      <div class="project-thumbnail small">
                        <Show
                          when={project.thumbnail}
                          fallback={
                            <img
                              src={PROJECT_PLACEHOLDER_THUMBNAIL}
                              alt={project.name}
                            />
                          }
                        >
                          <ThumbnailLoader
                            thumbnailUrl={project.thumbnail!}
                            altText={project.name}
                            placeholderUrl={PROJECT_PLACEHOLDER_THUMBNAIL}
                            getThumbnailBlob={getImageBlobUrl}
                          />
                        </Show>
                      </div>
                      <CustomTooltip
                        position="bottom"
                        class="flex-1 truncate leading-tight"
                        tooltipClass="opacity-90 !max-w-fit"
                        text={project.name}
                      >
                        {project.name}
                      </CustomTooltip>
                      {isSelected() ? (
                        <i class="icon-font tc-icon-check text-primary" />
                      ) : (
                        <i class="icon-font tc-icon-chevron-right h-full" />
                      )}
                    </li>
                  );
                }}
              </For>
            </ul>
          </Show>
        </Match>
      </Switch>
    </div>
  );
};
